import { useTranslation } from 'react-i18next';
import { generatePath, Navigate, useNavigate } from 'react-router-dom';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { useOwnPatientsQuery } from '@/hooks/useOwnPatientsQuery';
import { PetCard } from '../PetCard';
import { PortalPageHeader } from '../PortalPageHeader';

export function PetsPage() {
  const { t } = useTranslation('clients');
  const petsQuery = useOwnPatientsQuery(true);
  const pets = petsQuery.data === undefined ? [] : petsQuery.data;
  const onlyPet = pets.length === 1 ? pets[0] : undefined;

  if (petsQuery.isLoading === false && petsQuery.isError === false && onlyPet !== undefined) {
    return (
      <Navigate
        to={generatePath(APPLICATION_ROUTES.clientPortalPet, { patientId: onlyPet.id })}
        replace
      />
    );
  }

  return (
    <div className="p-4 sm:p-6 md:p-8">
      <PortalPageHeader
        title={t('portal.pages.pets.title')}
        helperText={t('portal.pages.pets.helper')}
      />
      {petsQuery.isLoading === true ? (
        <div className="py-16 flex justify-center">
          <LoadingSpinner className="w-7 h-7" />
        </div>
      ) : petsQuery.isError === true ? (
        <QueryErrorState
          onRetry={function retry() {
            petsQuery.refetch();
          }}
        />
      ) : pets.length === 0 ? (
        <PetsEmptyState />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {pets.map(function renderPet(pet) {
            return <PetCard key={pet.id} pet={pet} />;
          })}
        </div>
      )}
    </div>
  );
}

function PetsEmptyState() {
  const { t } = useTranslation('clients');
  const navigate = useNavigate();

  return (
    <Card as="section" className="text-center py-10">
      <span className="mx-auto w-12 h-12 rounded-xl bg-zoovpalelavender text-zooprimary flex items-center justify-center mb-3">
        <Icon name="PawPrint" />
      </span>
      <p className="text-sm font-bold text-zooindigo">{t('portal.pages.pets.emptyTitle')}</p>
      <p className="text-xs text-zoomuted mt-1 max-w-sm mx-auto">
        {t('portal.pages.pets.emptyDescription')}
      </p>
      <Button
        variant="outline"
        size="small"
        className="mt-4"
        onClick={function goToIntake() {
          navigate(APPLICATION_ROUTES.clientPortalIntake);
        }}
      >
        <Icon name="ClipboardList" />
        {t('portal.pages.pets.emptyAction')}
      </Button>
    </Card>
  );
}
